import React, { useState } from 'react';
import { Card, CardContent, Avatar, List, ListItem, ListItemAvatar, ListItemText, Box, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { 
  Warning as WarningIcon, 
  Error as ErrorIcon, 
  Check as CheckIcon,
  Memory as MemoryIcon,
  LinearScale as LinearScaleIcon,
  Code as CodeIcon,
  Info as InfoIcon,
  Storage as StorageIcon
} from '@mui/icons-material';

export const notifications = [];

export const aiEvents = [];

export const logEntries = [];

export default function Notifications() {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState('alerts');

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  const renderNotificationList = (items, type) => {
    if (items.length === 0) {
      const messages = {
        alerts: 'No alerts',
        logs: 'No system logs'
      };
      
      return (
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 3,
          color: 'text.secondary'
        }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {messages[activeTab] || 'No items'}
          </Typography>
          <Typography variant="caption">
            All systems running normally
          </Typography>
        </Box>
      );
    }

    return (
      <List dense disablePadding sx={{ maxHeight: '100%', overflow: 'auto' }}>
        {items.map((item) => (
          <ListItem 
            key={item.id} 
            sx={{ 
              py: 1.75,
              px: 3,
              borderBottom: `1px solid ${theme.palette.divider}`
            }}
          >
            <ListItemAvatar sx={{ minWidth: 42 }}>
              <Avatar sx={{ 
                bgcolor: item.severity === 'error' ? 'error.main' :
                        item.severity === 'warning' ? 'warning.main' :
                        item.severity === 'success' ? 'success.main' : 'info.main',
                width: 32,
                height: 32
              }}>
                {item.icon}
              </Avatar>
            </ListItemAvatar>
            <ListItemText 
              primary={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={500} color="text.primary" fontSize="0.9rem">
                    {item.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontSize="0.75rem">
                    {item.time}
                  </Typography>
                </Box>
              }
              secondary={item.details}
              secondaryTypographyProps={{
                fontSize: '0.8125rem',
                color: 'text.secondary',
                mt: 0.5
              }}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: 'background.paper',
      color: 'text.primary',
      borderRadius: 3,
      boxShadow: 'none',
      border: `1px solid ${theme.palette.divider}`
    }}>
      <Box sx={{ px: 3, pt: 3, pb: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="notification tabs"
            textColor="inherit"
            TabIndicatorProps={{
              style: {
                backgroundColor: theme.palette.success.main
              }
            }}
            sx={{
              '& .MuiTab-root': {
                color: 'text.secondary',
                fontWeight: 500,
                padding: '12px 16px',
                minWidth: 'auto',
                textTransform: 'none',
                fontSize: '0.9rem',
                marginRight: 2
              },
              '& .Mui-selected': {
                color: 'success.main',
                fontWeight: 600
              }
            }}
          >
            <Tab label="Alerts" value="alerts" />
            <Tab label="Logs" value="logs" />
          </Tabs>
        </Box>
      </Box>
      <CardContent sx={{ 
        flex: 1,
        overflowY: 'auto',
        p: 0,
        '&:last-child': { pb: 0 }
      }}>
        {activeTab === 'alerts' && renderNotificationList(notifications, 'alerts')}
        {activeTab === 'logs' && renderNotificationList(logEntries, 'logs')}
      </CardContent>
    </Card>
  );
}
